import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  ArrowLeft,
  Search,
  AlertCircle,
  Star
} from 'lucide-react';
import toast from 'react-hot-toast';

// 📝 COMMENT: Import your actual web Redux actions and services
import { userPreferences } from '../../actions/actions';
import { putUserPreferences } from '../../services/leadService';
import CustomFieldsBuilder, { CustomField as CustomFieldType } from './CustomFieldsBuilder';
import { COLOR_OPTIONS } from '../../config/color-options';
import OptionsManager from './forms/OptionsManager';
import ColorPickerField from './forms/ColorPickerField';
import logger from '../../utils/logger';

// 📝 COMMENT: Types for customization items - COMPLETE BACKEND MATCH
interface CustomField {
  name: string;
  type: string; // "text", "number", "amount", "date", "selection", "location", etc.
  value: string;
  isRequired: boolean;
  fieldOptions?: string[]; // For selection types
}

interface PreferenceItem {
  _id?: string;
  name: string;
  value: string;

  // Common flags
  color?: string;
  isDefault?: boolean;
  read_only?: boolean;
  readOnly?: boolean;        // Backend uses both read_only and readOnly

  // Status-specific
  isSaleStatus?: boolean;     // ⚠️ CRITICAL: Marks status as won/sale

  // Form field-specific (customForm)
  type?: string;              // Field type: text, name, email, phone, number, amount, date, etc.
  isRequired?: boolean;       // Make field mandatory
  options?: { name: string; value: string }[];  // Options for selection fields
  similarNames?: string[] | boolean;  // SYSTEM FIELD - Array of similar names (managed by backend)
  showWhen?: any;             // SYSTEM FIELD - Conditional display logic (managed by backend)
  isShowConditionally?: boolean;  // SYSTEM FIELD - Flag for conditional fields (managed by backend)

  // DEPRECATED FIELDS (kept for backward compatibility, but NOT used in forms)
  label?: string;             // REMOVED - Only use name field
  placeholder?: string;       // REMOVED - Not needed

  // Task/Activity-specific
  icon?: string;              // Icon name: "phone", "email", "meeting", "map-marker"
  notifyBefore?: string;      // Task notification: "15_minutes", "30_minutes", "45_minutes", "60_minutes"
  customFields?: CustomField[]; // Custom fields for activityType and taskType
}

interface CustomizationCardProps {
  title: string;
  type: string;
  description: string;
  icon: React.ReactNode;
  color?: string;
}

// 📝 COMMENT: Form field types - ENHANCED with Group and Quantity
const FORM_FIELD_TYPES = [
  { name: 'Text', value: 'text' },
  { name: 'Name', value: 'name' },
  { name: 'Email', value: 'email' },
  { name: 'Phone', value: 'phone' },
  { name: 'Number', value: 'number' },
  { name: 'Amount', value: 'amount' },
  { name: 'Quantity', value: 'quantity' },
  { name: 'Date', value: 'date' },
  { name: 'Time', value: 'time' },
  { name: 'Selection (Dropdown)', value: 'selection' },
  { name: 'Multi-Selection', value: 'multi-selection' },
  { name: 'URL', value: 'url' },
  { name: 'Location', value: 'location' },
  { name: 'Textarea', value: 'textarea' },
  { name: 'Group', value: 'group' }
];

// Clean a single item to only include fields valid for its type
const cleanItem = (item: PreferenceItem, preferenceType: string): any => {
  // Start with fields common to all types - trim leading/trailing spaces
  const cleaned: any = { name: item.name?.trim(), value: item.value?.trim() };

  // Keep real MongoDB _id, strip client-generated ones
  if (item._id && /^[a-f\d]{24}$/i.test(item._id)) {
    cleaned._id = item._id;
  }

  switch (preferenceType) {
    case 'labels':
      if (item.color) cleaned.color = item.color;
      if (item.isDefault !== undefined) cleaned.isDefault = item.isDefault;
      break;

    case 'status':
      if (item.color) cleaned.color = item.color;
      if (item.isDefault !== undefined) cleaned.isDefault = item.isDefault;
      if (item.isSaleStatus !== undefined) cleaned.isSaleStatus = item.isSaleStatus;
      break;

    case 'activityType':
      if (item.readOnly !== undefined) cleaned.readOnly = item.readOnly;
      if (item.read_only !== undefined) cleaned.read_only = item.read_only;
      if (item.icon) cleaned.icon = item.icon;
      if (item.customFields) cleaned.customFields = item.customFields;
      break;

    case 'taskType':
      if (item.readOnly !== undefined) cleaned.readOnly = item.readOnly;
      if (item.read_only !== undefined) cleaned.read_only = item.read_only;
      if (item.icon) cleaned.icon = item.icon;
      if (item.notifyBefore) cleaned.notifyBefore = item.notifyBefore;
      if (item.customFields) cleaned.customFields = item.customFields;
      break;

    case 'customForm':
      if (item.type) cleaned.type = item.type;
      if (item.isRequired !== undefined) cleaned.isRequired = item.isRequired;
      if (item.label) cleaned.label = item.label;
      if (item.read_only !== undefined) cleaned.read_only = item.read_only;
      if (item.similarNames !== undefined) cleaned.similarNames = item.similarNames;
      if (item.showWhen !== undefined) cleaned.showWhen = item.showWhen;
      if (item.isShowConditionally !== undefined) cleaned.isShowConditionally = item.isShowConditionally;
      if (item.options && item.options.length > 0) cleaned.options = item.options;
      break;
  }

  return cleaned;
};

// Build payload: only send the changed type, cleaned
const buildPayload = (
  changedType: string,
  changedData: PreferenceItem[]
): Record<string, any> => {
  return {
    [changedType]: changedData.map((item: PreferenceItem) => cleanItem(item, changedType))
  };
};

type ViewMode = 'list' | 'add' | 'edit';

const CustomizationCard: React.FC<CustomizationCardProps> = ({
  title, 
  type, 
  description, 
  icon, 
  color = '#3b82f6' 
}) => {
  const dispatch = useDispatch();
  
  // 📝 COMMENT: Use your actual web Redux selectors
  const userPrefs = useSelector(
    (state: any) => state?.rootReducers?.user?.userPreferences || {}
  );

  // 📝 COMMENT: State management
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingItem, setEditingItem] = useState<PreferenceItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<PreferenceItem | null>(null);

  // 📝 COMMENT: Form state for add/edit - ENHANCED with all mobile properties
  const [formData, setFormData] = useState<PreferenceItem>({
    name: '',
    value: '',
    color: COLOR_OPTIONS[0].value,
    isDefault: false,
    isSaleStatus: false,
    type: 'text',
    isRequired: false,
    options: []
    // NOTE: read_only, similarNames, showWhen, icon, label, placeholder - SYSTEM FIELDS or REMOVED, not in form
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // 📝 COMMENT: Get current preferences based on type
  const currentPreferences = useMemo(() => {
    return userPrefs[type] || [];
  }, [userPrefs, type]);

  // 📝 COMMENT: Filter preferences by search term
  const filteredPreferences = useMemo(() => {
    if (!searchTerm.trim()) return currentPreferences;
    return currentPreferences.filter((item: PreferenceItem) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [currentPreferences, searchTerm]);

  // 📝 COMMENT: Initialize form when editing - ENHANCED for all field types
  useEffect(() => {
    if (viewMode === 'edit' && editingItem) {
      setFormData({ ...editingItem });
    } else if (viewMode === 'add') {
      // Get existing colors to auto-select next available one
      const existingColors = currentPreferences.map((item: PreferenceItem) => item.color).filter(Boolean);

      // Auto-select next available color from full palette
      const available = COLOR_OPTIONS.filter(c => !existingColors.includes(c.value));
      const nextColor = available.length > 0 ? available[0].value : COLOR_OPTIONS[0].value;

      const defaultFormData: PreferenceItem = {
        name: '',
        value: '',
        color: nextColor,
        isDefault: false,
        isSaleStatus: false
        // NOTE: read_only, similarNames, icon, label, placeholder - SYSTEM FIELDS or REMOVED, not set in form
      };

      // 📝 COMMENT: Add specific fields for lead form fields
      if (type === 'customForm') {
        defaultFormData.type = 'text';
        defaultFormData.isRequired = false;
        defaultFormData.options = [];
        // DON'T set similarNames, read_only, showWhen - these are SYSTEM fields
      }

      // 📝 COMMENT: Add specific fields for task types
      if (type === 'taskType') {
        defaultFormData.notifyBefore = '15_minutes';
        defaultFormData.customFields = [];
      }

      // 📝 COMMENT: Add specific fields for activity types
      if (type === 'activityType') {
        defaultFormData.customFields = [];
      }

      setFormData(defaultFormData);
    }

    // Reset form errors
    setFormErrors({});
  }, [viewMode, editingItem, type, currentPreferences]);

  // 📝 COMMENT: Form validation - ENHANCED to match mobile validation
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      errors.name = 'Name is required';
    }

    // 📝 COMMENT: Additional validation for lead form fields
    if (type === 'customForm') {
      if (!formData.type) {
        errors.type = 'Field type is required';
      }

      // 📝 COMMENT: Validate options for selection fields
      if (['selection', 'multi-selection'].includes(formData.type || '') &&
          (!formData.options || formData.options.length === 0)) {
        errors.options = 'At least one option is required for selection fields';
      }
    }

    // 📝 COMMENT: Check for duplicate names (trim both sides to catch "Label " vs "Label")
    const duplicateName = currentPreferences.find(
      (item: PreferenceItem) =>
        item.name.trim().toLowerCase() === formData.name.trim().toLowerCase() &&
        (viewMode !== 'edit' || item._id !== formData._id)
    );

    if (duplicateName) {
      errors.name = `${title} with this name already exists`;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, currentPreferences, title, viewMode, type]);

  // 📝 COMMENT: Handle form submission - MATCHES MOBILE PATTERN EXACTLY
  // Mobile does: dispatch(updateUserPrefrence()) which calls UserApi().updatePreference()  
  // Web does: dispatch(userPreferences()) + putUserPreferences() - SAME RESULT!
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors and try again');
      return;
    }

    setIsLoading(true);
    try {
      let updatedPreferences;

      // Trim name before saving
      const trimmedName = formData.name.trim();
      const trimmedValue = trimmedName.toLowerCase().replace(/\s+/g, '_');

      if (viewMode === 'edit') {
        updatedPreferences = currentPreferences.map((item: PreferenceItem) => {
          const isTarget = (item._id && formData._id) ? item._id === formData._id : item.value === formData.value;
          if (isTarget) {
            return { ...item, ...formData, name: trimmedName };
          }
          // If the edited item is being set as default, unset others
          if (formData.isDefault && item.isDefault) {
            return { ...item, isDefault: false };
          }
          return item;
        });
      } else {
        const newItem = {
          ...formData,
          name: trimmedName,
          value: trimmedValue,
          _id: `${type}_${Date.now()}`
        };
        // If new item is default, unset others
        if (newItem.isDefault) {
          updatedPreferences = [
            ...currentPreferences.map((p: PreferenceItem) => ({ ...p, isDefault: false })),
            newItem
          ];
        } else {
          updatedPreferences = [...currentPreferences, newItem];
        }
      }

      const cleanedPayload = buildPayload(type, updatedPreferences);
      logger.log('Save payload:', JSON.stringify(cleanedPayload));

      const response = await putUserPreferences(cleanedPayload);

      if (response?.data?.status) {
        // Update Redux state only after successful API response
        dispatch(userPreferences({ ...userPrefs, [type]: updatedPreferences }));
        toast.success(`${title} ${viewMode === 'edit' ? 'updated' : 'created'} successfully`);
        setViewMode('list');
        setEditingItem(null);
      } else {
        throw new Error(response?.data?.message || 'Failed to save item');
      }

    } catch (error: any) {
      logger.error('Error saving preference:', error?.response?.data || error);
      toast.error(error?.response?.data?.message || error?.message || `Failed to ${viewMode === 'edit' ? 'update' : 'create'} ${title.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, viewMode, currentPreferences, type, title, dispatch, userPrefs]);

  // 📝 COMMENT: Handle delete - MATCHES MOBILE PATTERN (Redux + API)
  const handleDelete = useCallback(async (item: PreferenceItem) => {
    // Check both isDefault and readOnly/read_only flags
    if (item.isDefault) {
      toast.error('Cannot delete default items');
      return;
    }

    if (item.readOnly || item.read_only) {
      toast.error('Cannot delete read-only items');
      return;
    }

    setIsLoading(true);
    try {
      // Remove only the target item: match by _id if available, otherwise by value
      const updatedPreferences = currentPreferences.filter(
        (pref: PreferenceItem) => {
          if (item._id && pref._id) return pref._id !== item._id;
          return pref.value !== item.value;
        }
      );

      // Safety: never send an empty array — API will reject it
      if (updatedPreferences.length === 0) {
        toast.error(`Cannot delete the last ${title.toLowerCase()}`);
        setIsLoading(false);
        return;
      }

      const cleanedPayload = buildPayload(type, updatedPreferences);
      logger.log('Delete payload:', JSON.stringify(cleanedPayload));

      const response = await putUserPreferences(cleanedPayload);

      if (response?.data?.status) {
        dispatch(userPreferences({ ...userPrefs, [type]: updatedPreferences }));
        toast.success(`${title} deleted successfully`);
        setDeleteConfirm(null);
      } else {
        throw new Error(response?.data?.message || 'Failed to delete item');
      }

    } catch (error: any) {
      logger.error('Error deleting preference:', error?.response?.data || error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to delete item');
    } finally {
      setIsLoading(false);
    }
  }, [currentPreferences, type, title, dispatch, userPrefs]);

  // Set a different item as default (unsets old default, sets new one)
  const handleSetDefault = useCallback(async (item: PreferenceItem) => {
    if (item.isDefault) return; // already default

    setIsLoading(true);
    try {
      // Unset old default, set new default
      const updatedPreferences = currentPreferences.map((pref: PreferenceItem) => ({
        ...pref,
        isDefault: (pref._id && item._id) ? pref._id === item._id : pref.value === item.value
      }));

      const cleanedPayload = buildPayload(type, updatedPreferences);
      logger.log('Set default payload:', JSON.stringify(cleanedPayload));

      const response = await putUserPreferences(cleanedPayload);

      if (response?.data?.status) {
        dispatch(userPreferences({ ...userPrefs, [type]: updatedPreferences }));
        toast.success(`"${item.name}" set as default`);
      } else {
        throw new Error(response?.data?.message || 'Failed to update default');
      }
    } catch (error: any) {
      logger.error('Error setting default:', error?.response?.data || error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to set default');
    } finally {
      setIsLoading(false);
    }
  }, [currentPreferences, type, dispatch, userPrefs]);

  // 📝 COMMENT: Handle form field changes
  const handleFieldChange = useCallback((field: keyof PreferenceItem, value: any) => {
    setFormData(prev => {
      const updated = { ...prev };

      if (field === 'name') {
        updated.name = value;
        // Auto-generate value from name by trimming spaces before/after and replacing spaces with underscores
        updated.value = value.trim().toLowerCase().replace(/\s+/g, '_');
      } else {
        // Use type assertion to handle different field types
        (updated as any)[field] = value;
      }

      return updated;
    });

    // Clear field error
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [formErrors]);

  // 📝 COMMENT: Render card header
  const renderCardHeader = () => (
    <div 
      onClick={() => setIsExpanded(!isExpanded)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px',
        cursor: 'pointer',
        borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          backgroundColor: `${color}15`,
          borderRadius: '12px'
        }}>
          {icon}
        </div>
        <div>
          <h3 style={{ 
            margin: '0 0 4px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827' 
          }}>
            {title}
          </h3>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#6b7280' 
          }}>
            {description}
          </p>
        </div>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{
          fontSize: '12px',
          fontWeight: '500',
          color: color,
          backgroundColor: `${color}15`,
          padding: '4px 8px',
          borderRadius: '12px'
        }}>
          {currentPreferences.length} items
        </span>
        
        <div style={{
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </div>
  );

  // 📝 COMMENT: Render expanded content
  const renderExpandedContent = () => {
    if (!isExpanded) return null;

    if (viewMode === 'add' || viewMode === 'edit') {
      return renderForm();
    }
    return renderList();
  };

  // 📝 COMMENT: Render preferences list
  const renderList = () => (
    <div style={{ padding: '20px 24px' }}>
      {/* Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        gap: '12px'
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, maxWidth: '250px' }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }} />
          <input
            type="text"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px 6px 32px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none'
            }}
          />
        </div>

        {/* Add Button */}
        <button
          onClick={() => setViewMode('add')}
          disabled={isLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: color,
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <Plus size={14} />
          Add {title}
        </button>
      </div>

      {/* List Items */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {filteredPreferences.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280'
          }}>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
              {searchTerm.trim() 
                ? `No ${title.toLowerCase()} match your search.`
                : `No ${title.toLowerCase()} created yet.`
              }
            </p>
            {!searchTerm.trim() && (
              <button
                onClick={() => setViewMode('add')}
                style={{
                  backgroundColor: color,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Add First {title}
              </button>
            )}
          </div>
        ) : (
          filteredPreferences.map((item: PreferenceItem, index: number) => (
            <div
              key={item._id || item.value || index}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                marginBottom: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
            >
              {/* Color Indicator */}
              {item.color && (
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color,
                  borderRadius: '50%',
                  marginRight: '10px'
                }} />
              )}

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {item.name}
                  </span>
                  
                  {item.isDefault && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#059669',
                      backgroundColor: '#d1fae5',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Default
                    </span>
                  )}
                  
                  {item.isSaleStatus && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Sale Status
                    </span>
                  )}
                  
                  {type === 'customForm' && item.isRequired && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Required
                    </span>
                  )}

                  {(item.readOnly || item.read_only) && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#6b7280',
                      backgroundColor: '#f3f4f6',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Read-Only
                    </span>
                  )}

                  {item.notifyBefore && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#2563eb',
                      backgroundColor: '#dbeafe',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      🔔 {item.notifyBefore.replace('_', ' ')}
                    </span>
                  )}
                </div>
                
                {type === 'customForm' && item.type && (
                  <p style={{
                    margin: '2px 0 0 0',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    Type: {FORM_FIELD_TYPES.find(t => t.value === item.type)?.name || item.type} | Value: {item.value}
                    {item.options && item.options.length > 0 && ` | Options: ${item.options.length}`}
                  </p>
                )}

                {(type === 'activityType' || type === 'taskType') && (
                  <p style={{
                    margin: '2px 0 0 0',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    Value: {item.value}
                    {item.customFields && item.customFields.length > 0 && ` | Custom Fields: ${item.customFields.length}`}
                    {item.icon && ` | Icon: ${item.icon}`}
                  </p>
                )}

{/* Value is internal for labels/status - not displayed */}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {/* Set as Default (labels & status only) */}
                {(type === 'labels' || type === 'status') && (
                  <button
                    onClick={() => handleSetDefault(item)}
                    disabled={item.isDefault || item.isSaleStatus || isLoading}
                    title={item.isDefault ? 'Current default' : item.isSaleStatus ? 'Sale status cannot be default' : 'Set as default'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      backgroundColor: item.isDefault ? '#fef3c7' : '#ffffff',
                      border: `1px solid ${item.isDefault ? '#fbbf24' : '#d1d5db'}`,
                      borderRadius: '4px',
                      cursor: (item.isDefault || item.isSaleStatus) ? 'default' : 'pointer',
                      opacity: (isLoading || item.isSaleStatus) ? 0.5 : 1
                    }}
                  >
                    <Star
                      size={12}
                      fill={item.isDefault ? '#f59e0b' : 'none'}
                      color={item.isDefault ? '#f59e0b' : '#9ca3af'}
                    />
                  </button>
                )}

                <button
                  onClick={() => {
                    setEditingItem(item);
                    setViewMode('edit');
                  }}
                  disabled={isLoading || item.readOnly || item.read_only}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    backgroundColor: (item.readOnly || item.read_only) ? '#f9fafb' : '#ffffff',
                    border: `1px solid ${(item.readOnly || item.read_only) ? '#e5e7eb' : '#d1d5db'}`,
                    borderRadius: '4px',
                    cursor: (isLoading || item.readOnly || item.read_only) ? 'not-allowed' : 'pointer',
                    opacity: (isLoading || item.readOnly || item.read_only) ? 0.5 : 1
                  }}
                >
                  <Edit3 size={12} className={(item.readOnly || item.read_only) ? 'text-gray-400' : 'text-gray-600'} />
                </button>

                <button
                  onClick={() => setDeleteConfirm(item)}
                  disabled={item.isDefault || item.readOnly || item.read_only}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    backgroundColor: (item.isDefault || item.readOnly || item.read_only) ? '#f9fafb' : '#ffffff',
                    border: `1px solid ${(item.isDefault || item.readOnly || item.read_only) ? '#e5e7eb' : '#fecaca'}`,
                    borderRadius: '4px',
                    cursor: (item.isDefault || item.readOnly || item.read_only) ? 'not-allowed' : 'pointer',
                    opacity: (item.isDefault || item.readOnly || item.read_only) ? 0.5 : 1
                  }}
                >
                  <Trash2 size={12} className={(item.isDefault || item.readOnly || item.read_only) ? 'text-gray-400' : 'text-red-500'} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // 📝 COMMENT: Render add/edit form
  const renderForm = () => (
    <div style={{ padding: '20px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setViewMode('list')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
            color: '#6b7280',
            marginBottom: '12px'
          }}
        >
          <ArrowLeft size={14} />
          Back to List
        </button>

        <h4 style={{ 
          margin: '0 0 4px 0', 
          fontSize: '16px', 
          fontWeight: '600', 
          color: '#111827' 
        }}>
          {viewMode === 'edit' ? `Edit ${title}` : `Add New ${title}`}
        </h4>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div style={{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px'
        }}>
          {/* Name Field */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder={`Enter ${title.toLowerCase()} name`}
              maxLength={50}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: `1px solid ${formErrors.name ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none'
              }}
            />
            {formErrors.name && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
                fontSize: '11px',
                color: '#dc2626'
              }}>
                <AlertCircle size={12} />
                {formErrors.name}
              </div>
            )}
          </div>

          {/* Lead Form Fields - Additional Fields */}
          {type === 'customForm' && (
            <>
              {/* Field Type */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Field Type <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={formData.type || 'text'}
                  onChange={(e) => {
                    logger.log('🎯 Field type changed to:', e.target.value);
                    handleFieldChange('type', e.target.value);
                    // Clear options when changing field type
                    if (!['selection', 'multi-selection'].includes(e.target.value)) {
                      logger.log('❌ Clearing options (not a selection field)');
                      setFormData(prev => ({ ...prev, options: [] }));
                    } else {
                      logger.log('✅ Selection field selected - options should appear');
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: `1px solid ${formErrors.type ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                >
                  {FORM_FIELD_TYPES.map((fieldType) => (
                    <option key={fieldType.value} value={fieldType.value}>
                      {fieldType.name}
                    </option>
                  ))}
                </select>
                {formErrors.type && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '11px',
                    color: '#dc2626'
                  }}>
                    <AlertCircle size={12} />
                    {formErrors.type}
                  </div>
                )}
              </div>

              {/* Options Management for Selection Fields */}
              {['selection', 'multi-selection'].includes(formData.type || '') && (
                <OptionsManager
                  options={formData.options || []}
                  onChange={(opts) => handleFieldChange('options', opts)}
                  color={color}
                  error={formErrors.options}
                />
              )}

              {/* Required Checkbox */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={!!formData.isRequired}
                    onChange={(e) => handleFieldChange('isRequired', e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  Mark as Required Field
                </label>
              </div>
            </>
          )}

          {/* Task Type specific fields */}
          {type === 'taskType' && (
            <>
              {/* Notify Before Dropdown */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Notify Before
                </label>
                <select
                  value={formData.notifyBefore || '15_minutes'}
                  onChange={(e) => handleFieldChange('notifyBefore', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                >
                  <option value="15_minutes">15 minutes before due time</option>
                  <option value="30_minutes">30 minutes before due time</option>
                  <option value="45_minutes">45 minutes before due time</option>
                  <option value="60_minutes">60 minutes before due time</option>
                </select>
              </div>

              {/* Read-Only Checkbox */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={!!formData.readOnly}
                    onChange={(e) => handleFieldChange('readOnly', e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  Mark as Read-Only (prevent deletion)
                </label>
              </div>

              {/* Custom Fields Builder */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Custom Fields
                </label>
                <CustomFieldsBuilder
                  customFields={formData.customFields || []}
                  onChange={(fields) => handleFieldChange('customFields', fields)}
                  color={color}
                />
              </div>
            </>
          )}

          {/* Activity Type specific fields */}
          {type === 'activityType' && (
            <>
              {/* Read-Only Checkbox */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={!!formData.readOnly}
                    onChange={(e) => handleFieldChange('readOnly', e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  Mark as Read-Only (prevent deletion)
                </label>
              </div>

              {/* Custom Fields Builder */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Custom Fields
                </label>
                <CustomFieldsBuilder
                  customFields={formData.customFields || []}
                  onChange={(fields) => handleFieldChange('customFields', fields)}
                  color={color}
                />
              </div>
            </>
          )}

          {/* Color Field (for labels and status) */}
          {(type === 'labels' || type === 'status') && (
            <ColorPickerField
              value={formData.color || ''}
              onChange={(c) => handleFieldChange('color', c)}
              colorOptions={COLOR_OPTIONS}
            />
          )}

          {/* Default checkbox (labels & status) — cannot be default AND sale status */}
          {(type === 'labels' || type === 'status') && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: formData.isSaleStatus ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: formData.isSaleStatus ? '#9ca3af' : '#374151'
              }}>
                <input
                  type="checkbox"
                  checked={!!formData.isDefault}
                  disabled={!!formData.isSaleStatus}
                  onChange={(e) => handleFieldChange('isDefault', e.target.checked)}
                  style={{ width: '14px', height: '14px' }}
                />
                Set as Default
              </label>
            </div>
          )}

          {/* Status-specific: sale status — cannot be sale AND default */}
          {type === 'status' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: formData.isDefault ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: formData.isDefault ? '#9ca3af' : '#374151'
              }}>
                <input
                  type="checkbox"
                  checked={!!formData.isSaleStatus}
                  disabled={!!formData.isDefault}
                  onChange={(e) => handleFieldChange('isSaleStatus', e.target.checked)}
                  style={{ width: '14px', height: '14px' }}
                />
                Mark as Sale Status
              </label>
              <p style={{
                margin: '4px 0 0 20px',
                fontSize: '11px',
                color: '#6b7280',
                lineHeight: '1.4'
              }}>
                Final status when a lead is considered closed. Used for sales target calculations.
              </p>
            </div>
          )}

          {/* Preview */}
          {formData.name && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Preview
              </label>
              <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '12px'
              }}>
                {/* Field Preview */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px'
                }}>
                  {formData.color && (type === 'labels' || type === 'status') && (
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: formData.color,
                      borderRadius: '50%'
                    }} />
                  )}
                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>
                    {formData.name}
                  </span>
                  {formData.isDefault && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#059669',
                      backgroundColor: '#d1fae5',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Default
                    </span>
                  )}
                  {formData.isSaleStatus && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Sale Status
                    </span>
                  )}
                  {type === 'customForm' && formData.isRequired && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Required
                    </span>
                  )}
                </div>

                {/* Form Field Preview for customForm */}
                {type === 'customForm' && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Field Type: <strong>{FORM_FIELD_TYPES.find(t => t.value === formData.type)?.name}</strong>
                    </div>

                    {/* Mock Input Preview */}
                    {formData.type === 'textarea' ? (
                      <textarea
                        disabled
                        placeholder={`Enter ${formData.name}`}
                        style={{
                          width: '100%',
                          height: '60px',
                          padding: '6px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: '#f9fafb',
                          resize: 'none'
                        }}
                      />
                    ) : ['selection', 'multi-selection'].includes(formData.type || '') ? (
                      <div>
                        <select
                          disabled
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: '#f9fafb'
                          }}
                        >
                          <option>{`Select ${formData.name}`}</option>
                          {formData.options?.map((option, i) => (
                            <option key={i} value={option.value}>{option.name}</option>
                          ))}
                        </select>
                        {formData.options && formData.options.length > 0 && (
                          <div style={{
                            fontSize: '10px',
                            color: '#6b7280',
                            marginTop: '4px'
                          }}>
                            Options: {formData.options.map(opt => opt.name).join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <input
                        type={['number', 'amount'].includes(formData.type || '') ? 'number' :
                              ['email'].includes(formData.type || '') ? 'email' :
                              ['date'].includes(formData.type || '') ? 'date' :
                              ['time'].includes(formData.type || '') ? 'time' : 'text'}
                        disabled
                        placeholder={`Enter ${formData.name}`}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: '#f9fafb'
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
            paddingTop: '12px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              disabled={isLoading}
              style={{
                backgroundColor: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: color,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              <Save size={14} />
              {isLoading 
                ? `${viewMode === 'edit' ? 'Updating' : 'Creating'}...`
                : `${viewMode === 'edit' ? 'Update' : 'Create'} ${title}`
              }
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      overflow: 'hidden'
    }}>
      {renderCardHeader()}
      {renderExpandedContent()}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '350px',
            width: '90%'
          }}>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#111827' 
            }}>
              Delete {title}
            </h4>
            <p style={{
              margin: '0 0 16px 0',
              fontSize: '13px',
              color: '#6b7280'
            }}>
              Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isLoading}
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizationCard;